/**
 * Form UI helpers
 */

import { escapeAttr, escapeHtml } from '../utils.js';

/**
 * Sanitize a string for use as an HTML element ID.
 * Replaces characters that are problematic in CSS selectors (used by jQuery).
 */
export function sanitizeId(str: string): string {
  // Replace characters that have special meaning in CSS selectors
  // = [ ] : . # are all problematic
  return str.replace(/[=\[\]:\.#\s]/g, '_');
}

/**
 * Attach a submit handler to a form with automatic loading overlay.
 * Shows a spinner overlay on the form's container while the async handler
 * runs. Prevents double-submission and gives clear visual feedback.
 */
export function formSubmitHandler(
  form: HTMLElement,
  handler: (e: Event) => Promise<void>
): void {
  form.addEventListener('submit', async (e: Event) => {
    e.preventDefault();
    const btn = (form as HTMLFormElement).querySelector('[type="submit"]') as
      | HTMLInputElement
      | HTMLButtonElement
      | null;
    if (!btn || btn.disabled) return;
    btn.disabled = true;

    // Show loading overlay on the closest positioned container
    const container = form.closest('.form-container') || form.parentElement || form;
    const el = container as HTMLElement;
    const prevPosition = el.style.position;
    if (getComputedStyle(el).position === 'static') {
      el.style.position = 'relative';
    }
    const overlay = document.createElement('div');
    overlay.className = 'form-loading-overlay';
    overlay.innerHTML = '<div class="form-spinner"></div>';
    el.appendChild(overlay);

    try {
      await handler(e);
    } finally {
      btn.disabled = false;
      overlay.remove();
      el.style.position = prevPosition;
    }
  });
}

export interface DropdownItem {
  name: string;
  func: string;
  divider?: boolean;
  icon?: string;
  danger?: boolean;
}

/**
 * Generate a button HTML
 */
export function generateButton(name: string, onclick: string): string {
  return `<button class="ui-button" onclick="${escapeAttr(onclick)}">${escapeHtml(name)}</button>`;
}

/**
 * Generate a split button with dropdown
 */
export function generateSplitButton(
  primaryName: string,
  primaryFunc: string,
  dropdownItems: DropdownItem[]
): string {
  let dropdown = '<div class="dropdown-menu">';
  for (const item of dropdownItems) {
    if (item.divider) {
      dropdown += '<div class="divider"></div>';
    } else {
      const cls = item.danger ? ' class="danger"' : '';
      const icon = item.icon ? `<span class="dropdown-icon">${item.icon}</span> ` : '';
      dropdown += `<button${cls} onclick="${escapeAttr(item.func)}">${icon}${escapeHtml(item.name)}</button>`;
    }
  }
  dropdown += '</div>';

  return `<div class="split-btn">
    <button class="ui-button primary-btn" onclick="${escapeAttr(primaryFunc)}">${escapeHtml(primaryName)}</button>
    <button class="ui-button dropdown-toggle">&#9662;</button>
    ${dropdown}
  </div>`;
}

/**
 * Generate a combo/select dropdown
 */
export function generateComboSelect(
  id: string,
  selectedKey: string | number,
  data: Record<string | number, string>
): string {
  let select = `<select id="${escapeAttr(id)}">`;
  for (const [key, value] of Object.entries(data)) {
    const selected = key == selectedKey ? 'selected' : '';
    select += `<option ${selected} value="${escapeAttr(key)}">${escapeHtml(value)}</option>`;
  }
  select += '</select>';
  return select;
}

/**
 * Generate a multi-select checkbox group
 */
export function generateMultiSelect(
  name: string,
  keys: string[],
  selectedKeys: string[],
  labels: Record<string, string>
): string {
  let html = '<div class="multi-select">';
  for (const key of keys) {
    const checked = selectedKeys.includes(key) ? 'checked' : '';
    const label = labels[key] || key;
    html += `<label class="checkbox-label">
      <input type="checkbox" name="${escapeAttr(name)}" value="${escapeAttr(key)}" ${checked}>
      ${escapeHtml(label)}
    </label>`;
  }
  html += '</div>';
  return html;
}

/**
 * Get selected values from a multi-select checkbox group
 */
export function getSelectedOptions(name: string): string[] {
  const selected: string[] = [];
  $(`input[name="${name}"]:checked`).each(function () {
    selected.push($(this).val() as string);
  });
  return selected;
}

/**
 * Generate a text input field
 */
export function generateTextInput(
  id: string,
  label: string,
  value: string,
  options?: {
    type?: string;
    min?: number;
    max?: number;
    step?: number;
    disabled?: boolean;
    placeholder?: string;
  }
): string {
  const type = options?.type || 'text';
  const attrs: string[] = [];

  if (options?.min !== undefined) attrs.push(`min="${options.min}"`);
  if (options?.max !== undefined) attrs.push(`max="${options.max}"`);
  if (options?.step !== undefined) attrs.push(`step="${options.step}"`);
  if (options?.disabled) attrs.push('disabled');
  if (options?.placeholder) attrs.push(`placeholder="${escapeAttr(options.placeholder)}"`);

  return `<label>${escapeHtml(label)}&nbsp;</label>
    <input type="${type}" id="${escapeAttr(id)}" name="${escapeAttr(id)}"
           value="${escapeAttr(value)}" ${attrs.join(' ')}><br>`;
}

/**
 * Generate a checkbox field
 */
export function generateCheckbox(
  id: string,
  label: string,
  checked: boolean,
  options?: {
    onclick?: string;
    disabled?: boolean;
  }
): string {
  const attrs: string[] = [];
  if (checked) attrs.push('checked');
  if (options?.disabled) attrs.push('disabled');
  if (options?.onclick) attrs.push(`onclick="${escapeAttr(options.onclick)}"`);

  return `<label>${escapeHtml(label)}&nbsp;</label>
    <span id="${escapeAttr(id)}_area">
      <input type="checkbox" id="${escapeAttr(id)}" ${attrs.join(' ')}>
    </span><br>`;
}

/**
 * Generate a toggle switch (styled checkbox slider)
 */
export function generateToggle(id: string, checked: boolean): string {
  return `<label class="toggle-switch"><input type="checkbox" id="${escapeAttr(id)}" ${checked ? 'checked' : ''}><span class="toggle-slider"></span></label>`;
}

/**
 * Generate a list of toggle switches for a set of known values.
 * Each toggle gets an id of `${prefix}_${item}` and a hidden input with class `${prefix}-toggle`.
 * Use `getToggleListSelected(prefix)` to collect the selected values.
 */
export function generateToggleList(prefix: string, items: string[], selected: string[], labels?: Record<string, string>): string {
  if (items.length === 0) return '<span style="color:#888">None defined</span>';
  let html = '';
  for (const item of items) {
    // Sanitize the ID to avoid CSS selector issues (e.g., hv-spinlocks=0x1fff)
    const id = sanitizeId(`${prefix}_${item}`);
    const checked = selected.includes(item);
    const desc = labels?.[item];
    const label = desc ? `${escapeHtml(desc)}<br><code>${escapeHtml(item)}</code>` : escapeHtml(item);
    html += `<label>${label}</label><div class="form-value">${generateToggle(id, checked)}<input type="hidden" class="${escapeAttr(prefix)}-toggle" value="${escapeAttr(item)}" data-toggle-id="${escapeAttr(id)}"></div>`;
  }
  return html;
}

/**
 * Collect selected values from a toggle list generated by generateToggleList.
 */
export function getToggleListSelected(prefix: string): string[] {
  const selected: string[] = [];
  $(`.${prefix}-toggle`).each(function () {
    const toggleId = $(this).data('toggle-id') as string;
    if ($('#' + toggleId).is(':checked')) {
      selected.push($(this).val() as string);
    }
  });
  return selected;
}

/**
 * Generate a network prefix size <select> for auto-allocating CIDRs.
 * Options range from /29 (5 hosts) to /24 (253 hosts).
 * If includeNone is true, a "None" option with value "" is prepended.
 */
export function generateNetworkPrefixSelect(id: string, includeNone: boolean = false): string {
  let html = `<select id="${escapeAttr(id)}">`;
  if (includeNone) {
    html += '<option value="">None</option>';
  }
  html += '<option value="29">/29 (5 hosts)</option>';
  html += '<option value="28">/28 (13 hosts)</option>';
  html += '<option value="27">/27 (29 hosts)</option>';
  html += '<option value="26">/26 (61 hosts)</option>';
  html += '<option value="25">/25 (125 hosts)</option>';
  html += '<option value="24">/24 (253 hosts)</option>';
  html += '</select>';
  return html;
}

/**
 * Set selected values on a toggle list generated by generateToggleList.
 * Checks toggles whose values are in `selected`, unchecks the rest.
 */
export function setToggleListSelected(prefix: string, selected: string[]): void {
  const set = new Set(selected);
  $(`.${prefix}-toggle`).each(function () {
    const toggleId = $(this).data('toggle-id') as string;
    const value = $(this).val() as string;
    $('#' + toggleId).prop('checked', set.has(value));
  });
}
